/**
 * Types du contrat `/v1/console` (voir `CONTRAT-API.md` et `docs/dev/api.md`
 * § 12). Les paliers, modules, rôles et quotas viennent de `@artosera/shared`
 * — la même matrice et le même modèle que l'API, pas une copie qui diverge.
 */
import type {
  ExigencesVersions,
  Comptabilite,
  ComptabiliteAssocie,
  ComptabiliteDirection,
  Cout,
  Creance,
  Encaissement,
  EncaissementsDuMois,
  EtatAbonnement,
  EtatCreance,
  EtatJalon,
  FamilleModule,
  Jalon,
  LibelleTypeRecette,
  Module,
  MoisComptable,
  MoisComptableAssocie,
  NatureAbonnement,
  NomVueDAigle,
  Palier,
  PeriodeVueDAigle,
  RepartitionRecette,
  Revenu,
  SaisieMontant,
  ServiceComptable,
  Subscription,
  Tarif,
  ThemeRegie,
  TypeRecetteId,
  TypeRecetteResume,
  TypeRecetteVue,
  TypeTarif,
  VueDAigle,
} from '@artosera/shared';
import type { RoleConsole } from '@artosera/shared/permissions';
import type { NatureLot } from './lib/reprise.js';

export type {
  Comptabilite,
  ComptabiliteAssocie,
  ComptabiliteDirection,
  Encaissement,
  EncaissementsDuMois,
  EtatAbonnement,
  EtatCreance,
  EtatJalon,
  FamilleModule,
  LibelleTypeRecette,
  Module,
  MoisComptable,
  MoisComptableAssocie,
  NatureAbonnement,
  NomVueDAigle,
  Palier,
  PeriodeVueDAigle,
  RepartitionRecette,
  RoleConsole,
  SaisieMontant,
  ServiceComptable,
  TypeRecetteId,
  TypeRecetteResume,
  TypeRecetteVue,
  TypeTarif,
  VueDAigle,
};

/** Les états d'une galerie que la console affiche : une seule liste (`etatsGalerie.test.ts`). */
export const ETATS_GALERIE = [
  'active',
  'suspendue',
  'resiliee',
  'purge_en_cours',
  'purgee',
] as const;
export type EtatGalerie = (typeof ETATS_GALERIE)[number];
/** États d'une galerie fermée (ARTO-54) : plus rien ne s'y règle, comme `galerieFermee` côté API. */
export const ETATS_GALERIE_FERMEE = ['resiliee', 'purge_en_cours', 'purgee'] as const;
export function estGalerieFermee(etat: EtatGalerie): boolean {
  return (ETATS_GALERIE_FERMEE as readonly string[]).includes(etat);
}
/** États que `PATCH /galleries/:id` peut écrire : la résiliation a sa route dédiée (7.6, 7.7). */
export type EtatGalerieModifiable = Exclude<EtatGalerie, (typeof ETATS_GALERIE_FERMEE)[number]>;
export type EtatDomaine = 'a_verifier' | 'actif' | 'desactive';
export type EtatSauvegarde = 'reussie' | 'echouee' | 'en_cours';
/**
 * États réels de la file de travaux (`docs/dev/api.md` § 12.8).
 * `ignore_module_inactif` : le travail appartient à un module désactivé pour
 * sa galerie — il est écarté, pas en échec, et ne se relance pas (D21).
 */
export type EtatTravail =
  'en_attente' | 'en_cours' | 'reussi' | 'echoue' | 'abandonne' | 'ignore_module_inactif';

/**
 * Occupation du stockage objet, mesurée chaque nuit par le travail
 * `storage:measure` (`docs/dev/api.md` § 12). `null` tant qu'aucune mesure n'a
 * eu lieu — et `null` n'est pas zéro : on dit « pas encore mesuré » plutôt que
 * de laisser croire à un stockage vide.
 */
export interface StockageGalerie {
  octets: number;
  objets: number;
  mesureLe: string;
}

export interface GalerieListe {
  id: string;
  slug: string;
  nom: string;
  palier: Palier;
  etat: EtatGalerie;
  /** Espace de test (lot B) : hors de tout chiffre de pilotage et de facturation. */
  estTest: boolean;
  /** Liste EXPLICITE des modules complémentaires activés (D21). */
  modulesActifs: string[];
  /** Liste EFFECTIVE : cœur, activés disponibles aux dépendances satisfaites, moins les coupures. */
  modules: string[];
  creeLe: string;
  domaines: number;
  membres: number;
  dernierePublication: string | null;
  derniereSauvegarde: string | null;
  stockage: StockageGalerie | null;
}

/**
 * L'enregistrement que la galerie pose chez son registraire : un CNAME vers le
 * sous-domaine par défaut pour un sous-domaine (`www.galerie-x.fr`), des A
 * vers les IPv4 du serveur et des AAAA pour ses IPv6 attendues, pour un domaine
 * nu (`galerie-x.fr`, qui ne peut pas porter de CNAME). `valeurs` vide : IPv4 non configurée ;
 * `valeursAaaa` vide : aucun AAAA à poser.
 */
export type ConsigneDns =
  { type: 'CNAME'; valeur: string } | { type: 'A'; valeurs: string[]; valeursAaaa: string[] };

export interface Domaine {
  hote: string;
  etat: EtatDomaine;
  /** CNAME attendu : le sous-domaine par défaut. */
  cible: string;
  verifieLe: string | null;
  /** L'adresse de la galerie ; ses autres hôtes actifs y redirigent (301). */
  principal: boolean;
  /** Le sous-domaine `<slug>.artosera.gallery`, servi d'office. */
  parDefaut: boolean;
  /** `null` pour le sous-domaine par défaut. */
  dns: ConsigneDns | null;
}

export interface Membre {
  email: string;
  role: string;
  dernierAcces: string | null;
  /** Le compte n'a pas encore accepté son invitation (lot B). */
  invitationEnAttente: boolean;
  /** Le compte de la ligne : « Renvoyer l'invitation » le désigne (lot B). */
  compteId: string;
  droits: Record<
    'voirPrixArtiste' | 'coffreConfidentiel' | 'emettreFactures' | 'publierSite',
    boolean
  >;
}

export interface Sauvegarde {
  date: string;
  taille: number;
  etat: EtatSauvegarde;
}

/**
 * État RÉEL d'un export (ARTO-54) : lu au registre et recoupé avec le
 * stockage, jamais déduit du seul succès du travail. `manquant` = annoncé
 * prêt, archive introuvable ; `supprime` = archive éliminée (rétention,
 * purge).
 */
export type EtatExport =
  | 'en_attente'
  | 'en_cours'
  | 'verification'
  | 'pret'
  | 'incomplet'
  | 'echoue'
  | 'manquant'
  | 'supprime';

export type OrigineExport = 'console' | 'mensuel' | 'resiliation';

export interface ExportGalerie {
  /** `null` tant que le travail n'a pas commencé. */
  id: string | null;
  jobId: string;
  origine: OrigineExport;
  /** Export final de résiliation : le seul qui puisse autoriser la purge. */
  final: boolean;
  etat: EtatExport;
  demandeLe: string;
  /** Date de dépôt lue sur l'archive. */
  pretLe: string | null;
  /** Fin de validité du lien de sept jours. */
  expireLe: string | null;
  /** Taille lue sur l'archive déposée. */
  octets: number | null;
  sha256: string | null;
  fichiers: number | null;
  /** Premier courriel TRANSMIS pour cette version (un courriel capturé ne compte pas). */
  envoyeLe: string | null;
  /** Premier téléchargement ENTIER de cette version par un membre de la direction. */
  telechargeLe: string | null;
  /** Premier accusé de réception de cette version : la seule remise. */
  accuseLe: string | null;
  erreur: string | null;
}

/**
 * Renvoi d'une archive à la direction (ARTO-54). La console ne voit jamais le
 * lien ni l'archive (D14) : seulement combien de membres ont été servis.
 */
export interface RenvoiExport {
  destinataires: number;
  transmis: number;
  expireLe: string;
}

export interface AccesSupport {
  actif: boolean;
  expireLe: string;
}

/**
 * Les droits qu'une demande d'accès de support peut porter (`schemaDemandeSupport`
 * côté API). L'Équipe est celui qui règle et publie le site d'une galerie ; la
 * direction de la galerie le voit avant d'accorder.
 */
export type RoleAccesSupport = 'lecture' | 'equipe';

export interface Quotas {
  stockageGo: number;
  stockageUtiliseGo: number;
  /** `null` = illimité, comme `utilisateurs` dans `@artosera/shared` (quota du palier Groupe). */
  membresMax: number | null;
}

/**
 * Fiche complète : reprend les champs de la liste, mais `domaines` et
 * `membres` y sont les listes détaillées (pas des dénombrements).
 */
export interface GalerieFiche {
  id: string;
  slug: string;
  nom: string;
  palier: Palier;
  etat: EtatGalerie;
  /** Espace de test (lot B) : hors de tout chiffre de pilotage et de facturation. */
  estTest: boolean;
  modulesActifs: string[];
  modules: string[];
  creeLe: string;
  dernierePublication: string | null;
  derniereSauvegarde: string | null;
  stockage: StockageGalerie | null;
  groupe: string | null;
  quotas: Quotas;
  domaines: Domaine[];
  membres: Membre[];
  sauvegardes: Sauvegarde[];
  exports: ExportGalerie[];
  supportAccess: AccesSupport | null;
  /** Purge différée d'une galerie résiliée ; `null` tant qu'elle ne l'est pas. */
  purge: PurgeGalerie | null;
  /** Thème (design) de la régie de la galerie (lot C). */
  theme: ThemeRegie;
  /** Les utilisateurs de la galerie peuvent-ils choisir un autre thème ? */
  choixThemeClient: boolean;
}

/** Réponse de `PUT /galleries/:id/apparence` (lot C). */
export interface ApparenceGalerie {
  id: string;
  theme: ThemeRegie;
  choixThemeClient: boolean;
}

/** Où en est la purge d'une galerie résiliée (7.7, ARTO-54) : manuelle, jamais planifiée. */
export interface PurgeGalerie {
  resilieeLe: string | null;
  /** Fin du délai de quatre-vingt-dix jours : la purge peut être LANCÉE à partir de là. */
  possibleLe: string | null;
  /** Premier accusé de réception de l'export final `pret`. */
  accuseLe: string | null;
  demandeeLe: string | null;
  demandeePar: string | null;
  /** Entrée en purge : la galerie n'accueille plus rien. */
  debuteLe: string | null;
  /** Données vivantes effacées. */
  purgeeLe: string | null;
  /** Échéance de l'effacement complet (rétention des sauvegardes). */
  effacementCompletPrevuLe: string | null;
  /** Effacement complet constaté : plus aucune sauvegarde ne la porte. */
  effacementCompletLe: string | null;
  /** Lancée sans accusé, avec un motif, par la direction de Venio. */
  forcee: boolean;
  motif: string | null;
  /** Opérations admises en cours : la purge attend qu'il n'y en ait plus (R2). */
  operationsEnCours: number;
  /**
   * Purge d'un protocole antérieur dont le contrôle a échoué : la barrière
   * reste, la reprise est SUPERVISÉE (relecture ARTO-54 6, B1).
   */
  repriseSuperviseeRequise: { le: string; raison: string } | null;
  /**
   * Historique des dépôts de sauvegarde inconnu : pas de constat
   * d'effacement complet tant qu'il n'est pas vérifié (B2).
   */
  historiqueDepotsInconnu: boolean;
  /** Version de l'historique des dépôts (relecture 7, N2). */
  versionHistoriqueDepots: number;
}

/** Un module du registre, vu depuis une galerie (`GET /galleries/:id/modules`, D21). */
export interface ModuleVue {
  id: Module;
  famille: FamilleModule;
  /** `false` : pas encore livré, donc non activable. */
  disponible: boolean;
  dependances: Module[];
  /** Modules qui dépendent directement de celui-ci. */
  dependants: Module[];
  /** Dans la liste explicite de la galerie (toujours vrai pour le cœur). */
  active: boolean;
  /** Réellement ouvert : activé, disponible, dépendances là, pas coupé. */
  effectif: boolean;
  /** Coupé pour tout le parc (console ou variable de secours). */
  coupeGlobalement: boolean;
  /** Dépendances à activer d'abord. */
  dependancesManquantes: Module[];
}

export interface ReponseModules {
  id: string;
  palier: Palier;
  modulesActifs: string[];
  /**
   * Révision des modules de la galerie : renvoyée avec « appliquer le
   * palier », qui échoue en 409 si elle a bougé entre-temps (D21).
   */
  revisionModules?: number;
  /** `false` : les interrupteurs globaux n'ont pas pu être lus (échec fermé). */
  interrupteursLisibles?: boolean;
  /** Tout le registre, dans son ordre. */
  modules: ModuleVue[];
}

/** Une coupure posée depuis la console : qui, quand, pourquoi. */
export interface CoupureModule {
  module: string;
  motif: string;
  auteurEmail: string;
  coupeLe: string;
}

/** Interrupteurs globaux (`GET /interrupteurs`). */
export interface Interrupteurs {
  coupes: CoupureModule[];
  /**
   * Variable de secours `ARTOSERA_MODULES_COUPES`, lue au démarrage de l'API :
   * lecture seule ici, elle se retire en redéployant.
   */
  coupesSecours: string[];
  /**
   * Additif (paliers de développement) : par module optionnel, le palier qui
   * le débloque et son état. Absent d'une API plus ancienne : rien n'est alors
   * tenu pour verrouillé.
   */
  modules?: Array<{ module: string; palier: IdPalierDev; debloque: boolean }>;
}

/** Identifiants des paliers de développement (investisseurs associés), dans l'ordre. */
export type IdPalierDev = 'mvp' | 'v1' | 'v2' | 'finale';

/** Un module vu depuis les paliers (`GET /v1/console/paliers`). */
export interface ModulePalierVue {
  id: string;
  famille: 'coeur' | 'complementaire' | 'service';
  categorie: string | null;
  /** Débloqué par le palier : indépendant de l'urgence. */
  debloque: boolean;
  coupeUrgence: boolean;
  coupeSecours: boolean;
  disponible: boolean;
  /** Livré au catalogue ; ne vaut ni ouverture effective ni activation galerie. */
  livre: boolean;
  modifieLe: string | null;
  modifiePar: string | null;
  motif: string | null;
}

export interface PalierDevVue {
  id: IdPalierDev;
  /** `false` : le MVP (le cœur) est inclus, en lecture seule. */
  verrouillable: boolean;
  debloques: number;
  total: number;
  modules: ModulePalierVue[];
}

/** Versions minimale et conseillée des apps mobiles, et la révision à renvoyer avec toute écriture. */
export interface VersionsAppsVue {
  exigences: ExigencesVersions;
  revision: number;
}

/** Les quatre paliers et la révision à renvoyer avec toute écriture. */
export interface PaliersVue {
  paliers: PalierDevVue[];
  revision: number;
}

export interface NouvelleGalerie {
  slug: string;
  nom: string;
  palier: Palier;
  langue: 'fr' | 'en';
  direction: { email: string; nom: string };
  /** Case « Espace de test » (lot B). */
  estTest: boolean;
  /** Provisionnement étendu (ARTO-127) : facultatifs, défauts côté API. */
  modulesActifs?: string[];
  groupeSlug?: string;
  domaine?: string;
  /** `differee` : invitation créée, courriel envoyé plus tard par le renvoi. */
  invitation?: 'maintenant' | 'differee';
}

export interface Travail {
  id: string;
  type: string;
  etat: EtatTravail;
  tentatives: number;
  runAt: string;
  lastError: string | null;
  galleryId?: string;
}

/** Une page de `GET /v1/console/jobs` (50 travaux par page). */
export interface PageTravaux {
  items: Travail[];
  page: number;
  total: number;
  /** Les types de travaux qui existent, pour le filtre. */
  types: string[];
}

/** Ce que la console demande à la liste des travaux. */
export interface FiltresTravaux {
  galerie?: string | undefined;
  etat?: EtatTravail | undefined;
  type?: string | undefined;
  /** Écarte les travaux ignorés (module inactif), sauf si `etat` les demande. */
  masquerIgnores?: boolean | undefined;
  page?: number | undefined;
}

export interface JournalEntree {
  date: string;
  acteur: string;
  action: string;
  cible: string;
  /** Le texte `cle=valeur · …` de l'API : le repli quand `metadonnees` manque. */
  details: string | null;
  /** Les métadonnées de la ligne, structurées (avant / après compris). */
  metadonnees?: Record<string, unknown>;
}

export interface Sante {
  status: 'ok' | 'degrade';
  version: string;
  environment: string;
  uptime: number;
  plateforme: { ok: boolean; etat: number; pingMs: number | null; base: string | null };
  galeries: { connexionsOuvertes: number; inactiviteMaximaleMs: number; bases: string[] };
  derniereMigration: { id: string; appliqueeLe: string } | null;
  verifieLe: string;
}

export interface CompteConsole {
  id: string;
  email: string;
  nom: string;
  langue: 'fr' | 'en';
  rolePlateforme: RoleConsole | null;
  deuxiemeFacteurActive: boolean;
  graceMfaJusquau: string | null;
}

export interface Me {
  compte: CompteConsole;
  galeries: unknown[];
  /** Le second facteur est exigé et pas encore actif (grâce en cours ou écoulée). */
  enrolementMfaRequis?: true;
  /** La session est confinée à l'enrôlement : seul `/securite` s'ouvre. */
  enrolementMfaSeulement?: true;
}

export interface ErreurApi {
  code: string;
  message: string;
  details: unknown;
}

// ── Saisies financières (ARTO-58) ─────────────────────────────────────────

/**
 * Un document du modèle de pilotage tel que la console le reçoit en JSON :
 * `id` au lieu de `_id`, dates en texte ISO. Le modèle reste celui de
 * `@artosera/shared` — aucune forme recopiée ici qui pourrait diverger.
 */
type EnJson<T> = T extends Date
  ? string
  : T extends readonly (infer U)[]
    ? EnJson<U>[]
    : T extends object
      ? { [K in keyof T]: EnJson<T[K]> }
      : T;
type Saisie<T extends { _id: unknown }> = EnJson<Omit<T, '_id'>> & { id: string };

export type TarifSaisie = Saisie<Tarif>;
export type AbonnementSaisie = Saisie<Subscription>;
export type RevenuSaisie = Saisie<Revenu>;
export type CreanceSaisie = Saisie<Creance>;
export type CoutSaisie = Saisie<Cout>;
export type JalonSaisie = Saisie<Jalon>;

/** Les six ressources saisies, par segment d'URL sous `/v1/console`. */
export interface SaisiesParRessource {
  tarifs: TarifSaisie;
  abonnements: AbonnementSaisie;
  revenus: RevenuSaisie;
  creances: CreanceSaisie;
  couts: CoutSaisie;
  jalons: JalonSaisie;
}
export type RessourceSaisie = keyof SaisiesParRessource;

/** Un corps d'écriture : seuls les champs envoyés comptent, `null` est une valeur. */
export type CorpsSaisie = Record<string, unknown>;

/** `GET /v1/console/associes` : un compte associé, ou invité en attente d'accepter. */
export interface CompteAssocie {
  id: string;
  email: string;
  nom: string;
  statut: string;
  actif: boolean;
  deuxiemeFacteurActive: boolean;
  derniereConnexionLe: string | null;
  invitationEnAttente: boolean;
}

/** Une ligne du partage : `null` = part à saisir. Points de base, 10 000 = 100 %. */
export interface BeneficiaireSaisie {
  id: string;
  libelle: string;
  compteId: string | null;
  partPointsDeBase: number | null;
  /** Reçoit la part que les exclusions des autres transfèrent (un seul). */
  recoitLeReste: boolean;
  /** Les galeries exclues de sa part, chacune à partir d'un mois. */
  galeriesExclues: ExclusionGalerieSaisie[];
}

/** Une galerie exclue de la part d'un bénéficiaire (`GET /partage/beneficiaires`). */
export interface ExclusionGalerieSaisie {
  galerieId: string;
  depuisMois: string;
}

/** Un versement par bénéficiaire et par mois : `null` = dû à saisir. */
export interface VersementSaisie {
  id: string;
  beneficiaireId: string;
  mois: string;
  montantDuCentimes: number | null;
  verseCentimes: number;
  verseLe: string | null;
}

/** `GET /partage/versements` : les lignes, et les mois clôturés parmi ceux demandés. */
export interface VersementsListe {
  items: VersementSaisie[];
  moisClotures: string[];
}

export interface InvitationAssocie {
  id: string;
  invitationExpireLe: string;
  invitationEnvoyee: boolean;
}

/** `GET /v1/console/groupes` : un exploitant et ses galeries actives, pour rattacher un forfait. */
export interface GroupeSaisie {
  id: string;
  nom: string;
  galeries: Array<{ id: string; nom: string }>;
}

export interface ConnecteurConsole {
  id: 'brevo' | 'artsy' | 'artnet' | 'stripe' | 'oidc';
  module: string;
  mode: 'simule' | 'reel';
  configure: boolean;
  finSecret: string | null;
  parametres: Record<string, string>;
  verifieLe: string | null;
  erreur: string | null;
  revision: number;
  moduleActif: boolean;
}
export interface ReponseConnecteursConsole {
  connecteurs: ConnecteurConsole[];
  simulation: boolean;
}

export interface EtatSsoConsole {
  moduleActif: boolean;
  identitesLiees: number;
  sessionsOuvertes: number;
  derniereConnexionLe: string | null;
}

/**
 * Une version de la grille de répartition d'un service
 * (`GET /v1/console/comptabilite/regles`) : applicable à partir d'un mois,
 * jusqu'à la version suivante du même service. `moisClotures` : les mois
 * clôturés qu'elle a répartis ; elle ne se corrige plus tant qu'il en reste.
 */
export interface VersionGrille {
  id: string;
  /** Un service de base, ou l'identifiant d'un type de recette créé. */
  service: TypeRecetteId;
  depuisMois: string;
  parts: Array<{ beneficiaireId: string; libelle: string; partPointsDeBase: number }>;
  moisClotures: string[];
  createdAt: string;
  updatedAt: string;
}

/** Le corps d'une version de la grille : des parts explicites, qui somment 10 000. */
export interface PartsGrille {
  beneficiaireId: string;
  partPointsDeBase: number;
}

// ── Dossiers d'ouverture (ARTO-127) — `docs/dev/api.md` § 12.13 ──────────────

export type EtatOuverture =
  | 'brouillon'
  | 'contrat_signe'
  | 'provisionne'
  | 'reprise'
  | 'recette'
  | 'bascule_planifiee'
  | 'en_service'
  | 'abandonne';

/** Les sous-étapes de `POST /ouvertures/:id/creer-galerie`, dans l'ordre. */
export type EtapeCreationOuverture =
  'provisionnement' | 'modules' | 'addons' | 'domaine' | 'invitations' | 'prereglages';

export interface EtapeOuverture {
  cle: EtapeCreationOuverture;
  /** `en_cours` : enfilée (préréglages) ou reportée (invitations à la bascule). */
  etat: 'en_cours' | 'fait' | 'sans_objet' | 'refuse';
  par: string;
  le: string;
  note: string;
}

export interface AdresseOuverture {
  ligne1: string;
  ligne2: string;
  codePostal: string;
  ville: string;
  pays: string;
}

export interface IdentiteOuverture {
  nomCommercial: string;
  slug: string;
  raisonSociale: string;
  formeJuridique: string;
  siren: string;
  siret: string;
  tvaIntracom: string;
  adresse: AdresseOuverture;
  langue: 'fr' | 'en';
  devise: string;
  fuseau: string;
  estTest: boolean;
}

export interface OffreOuverture {
  palier: Palier;
  modules: string[];
  addons: Array<{ addon: string; mode: 'achat' | 'location' | 'essai'; jusquAu: string | null }>;
  services: Array<'site' | 'boutique'>;
  groupeSlug: string | null;
  forfaitReprise: { texte: string; montantCentimes: number | null };
}

export interface EquipeOuverture {
  direction: { nom: string; email: string } | null;
  membres: Array<{
    nom: string;
    email: string;
    role: 'direction' | 'equipe' | 'lecture' | 'comptable';
  }>;
  invitations: 'maintenant' | 'a_la_bascule';
}

export interface DossierOuverture {
  id: string;
  numero: string;
  etat: EtatOuverture;
  /** À renvoyer avec toute modification ou transition : 409 si elle a bougé. */
  revision: number;
  galleryId: string | null;
  responsable: { id: string; email: string } | null;
  identite: IdentiteOuverture;
  offre: OffreOuverture;
  contrat: {
    reference: string;
    signeLe: string | null;
    piece: { sha256: string; octets: number; deposeLe: string; par: string } | null;
  };
  equipe: EquipeOuverture;
  prereglages: { mentionsComplementaires: string[]; domaine: string | null };
  jalons: {
    basculePrevueLe: string | null;
    basculeEffectueeLe: string | null;
    scelleLe: string | null;
    purgeSourcesLe: string | null;
  };
  etapes: EtapeOuverture[];
  notes: Array<{ texte: string; par: string; le: string }>;
  /** Résumé de la recette (ARTO-134) ; le détail est `GET …/recette`. */
  recette?: { validee: boolean; derogation: boolean; signalements: number };
  /** Où en est la bascule : étapes faites, dernière erreur, bail en cours. */
  bascule?: {
    etapes: EtapeBascule[];
    erreur: ErreurBascule | null;
    enCours: boolean;
  };
  /** Ce que `POST …/transition` accepterait maintenant (gardes non comprises). */
  transitionsPossibles: EtatOuverture[];
  creeLe: string;
  modifieLe: string;
}

// ── Recette et bascule (ARTO-134) ──────────────────────────────────────────

export type ControleRecette =
  'historique_localisations' | 'echantillon_fiches' | 'images_principales' | 'factures_anterieures';

export type EtapeBascule =
  'controle' | 'scellement' | 'mandat' | 'purge' | 'invitations' | 'mise_en_service';

export interface ErreurBascule {
  etape: EtapeBascule;
  raison: string;
  message: string;
  le: string;
}

/** Une nature au tableau de contrôle : `ecart` = total − (tout le reste). */
export interface LigneRecette {
  nature: NatureLot;
  lots: number;
  total: number;
  crees: number;
  completes: number;
  ignores: number;
  refuses: number;
  ecartes: number;
  ecart: number;
  /** Motifs d'écartement des lignes (accusés A4). */
  motifs: string[];
  /** Enregistrements laissés « à revoir » par les deltas (§ 8.9). */
  aRevoir: number;
}

export interface LotRecette {
  id: string;
  nature: NatureLot;
  etat: string;
}

/** `GET /v1/console/ouvertures/:id/recette`. */
export interface RecetteOuverture {
  dossierId: string;
  numero: string;
  etat: EtatOuverture;
  natures: LigneRecette[];
  aRevoir: number;
  lots: {
    executes: number;
    annules: number;
    misDeCote: Array<LotRecette & { motif: string }>;
    /** Lancés et pas finis : ils retiennent la recette et la bascule. */
    enCours: LotRecette[];
    /** Jamais lancés : mis de côté au passage en recette, avec son motif. */
    nonLances: LotRecette[];
    planActif: boolean;
  };
  controles: Array<{
    cle: ControleRecette;
    coche: boolean;
    par: string | null;
    le: string | null;
    note: string;
  }>;
  /** La validation de la galerie (A3), figée ; `null` tant qu'elle n'a pas validé. */
  validation: {
    par: string;
    le: string;
    natures: LigneRecette[];
    controles: ControleRecette[];
    pv: { disponible: boolean; le: string | null; rangeAuCoffre: boolean };
  } | null;
  derogation: { motif: string; par: string; le: string } | null;
  signalements: Array<{ texte: string; par: string; le: string }>;
  bascule: {
    prevueLe: string | null;
    le: string | null;
    effectueeLe: string | null;
    enCours: boolean;
    etapes: Array<{ cle: EtapeBascule; le: string; note: string }>;
    erreur: ErreurBascule | null;
  };
}

/** `POST …/basculer` : le dossier à jour et ce que CET appel a fait. */
export interface ReponseBascule extends DossierOuverture {
  /** Les étapes faites par CET appel ; vide quand tout l'était déjà. */
  faites: EtapeBascule[];
  invitationsEnvoyees: number;
  aRevoir: number | null;
}

/** Une ligne de `GET /ouvertures`. */
export interface ResumeOuverture {
  id: string;
  numero: string;
  etat: EtatOuverture;
  nomCommercial: string;
  slug: string;
  estTest: boolean;
  galleryId: string | null;
  responsable: string | null;
  basculePrevueLe: string | null;
  modifieLe: string;
}

export interface PageOuvertures {
  items: ResumeOuverture[];
  page: number;
  total: number;
}

/**
 * Corps de `POST /ouvertures` et de `PATCH /ouvertures/:id`. Chaque champ
 * reçu remplace l'ancien ; un champ absent n'est pas touché. `signeLe` et
 * `basculePrevueLe` sont des dates `AAAA-MM-JJ`.
 */
export interface SectionsOuverture {
  identite?: Partial<Omit<IdentiteOuverture, 'adresse'>> & {
    adresse?: Omit<AdresseOuverture, 'ligne2'> & { ligne2?: string };
  };
  offre?: Partial<OffreOuverture>;
  contrat?: { reference?: string; signeLe?: string | null };
  equipe?: Partial<EquipeOuverture>;
  prereglages?: { mentionsComplementaires?: string[]; domaine?: string | null };
  jalons?: { basculePrevueLe?: string | null };
}

export interface ModificationOuverture extends SectionsOuverture {
  revision: number;
  responsableId?: string | null;
  note?: string;
}

export interface ReponseTransitionOuverture extends DossierOuverture {
  /** Abandon d'un dossier dont la galerie existe : à résilier par son parcours. */
  resiliation?: { aFaire: true; galleryId: string };
}

export interface ReponseCreationGalerieOuverture extends DossierOuverture {
  /** Les sous-étapes faites par CET appel ; vide quand tout l'était déjà. */
  faites: EtapeCreationOuverture[];
  invitationsEnvoyees: number;
}

export interface DisponibiliteSlug {
  disponible: boolean;
  raison?: 'invalide' | 'reserve' | 'pris';
}

// ── Demandes de création de site — `GET /v1/console/demandes-site` ───────────

export type EtatDemandeSite = 'envoyee' | 'en_cours' | 'traitee' | 'abandonnee';
export type FiltreDemandesSite = 'ouvertes' | 'toutes' | EtatDemandeSite;
export type MoyenContactDemandeSite = 'email' | 'telephone' | 'visio';
export type DelaiDemandeSite = 'des_que_possible' | 'trois_mois' | 'six_mois' | 'sans_contrainte';

export interface DemandeSiteConsole {
  id: string;
  etat: EtatDemandeSite;
  siteExistant: boolean;
  urlActuelle: string | null;
  objectifs: string;
  langues: ('fr' | 'en')[];
  visiteVirtuelle: boolean;
  delai: DelaiDemandeSite;
  budget: string | null;
  message: string;
  contact: {
    nom: string;
    email: string;
    telephone: string | null;
    moyen: MoyenContactDemandeSite;
  };
  envoyeeLe: string;
  traiteeLe: string | null;
  galerie: { id: string; slug: string | null; nom: string | null };
  demandeurEmail: string | null;
}

// ── Diagnostics de l'app iOS — `GET /v1/console/diagnostics-ios` ─────────────

export type TypeDiagnosticIos = 'plantage' | 'blocage';
export type FiltreDiagnosticsIos = 'tous' | TypeDiagnosticIos;

export interface CadreDiagnosticIos {
  binaire: string;
  uuid: string;
  decalage: number;
  adresse: number;
  profondeur: number;
  echantillons: number | null;
}

interface ResumeDiagnosticIos {
  empreinte: string;
  type: TypeDiagnosticIos;
  app: { version: string; build: string; testflight: boolean };
  plantage: {
    typeException: number | null;
    codeException: number | null;
    signal: number | null;
    raisonFin: string | null;
    nomException: string | null;
  } | null;
  blocage: { dureeMs: number } | null;
}

/** Un défaut : les occurrences d'une même empreinte (même version, même haut de pile). */
export interface DefautIos extends ResumeDiagnosticIos {
  occurrences: number;
  premierLe: string;
  dernierLe: string;
  appareils: string[];
  systemes: string[];
  haut: CadreDiagnosticIos[];
}

/** La dernière occurrence d'un défaut, avec toutes ses piles. */
export interface DiagnosticIos extends ResumeDiagnosticIos {
  survenuLe: string;
  recuLe: string;
  systeme: string;
  appareil: string;
  architecture: string;
  fils: { attribue: boolean; cadres: CadreDiagnosticIos[] }[];
}
